import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Layout from "../Common/Layout";
import { showNotification } from "./utils/notification";

const DeliveryOrderList = () => {
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [approving, setApproving] = useState(false);

  const token = sessionStorage.getItem("token");

  // track previous count
  const previousCount = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchDeliveryOrders();

    // poll every 15 seconds
    intervalRef.current = setInterval(fetchDeliveryOrders, 15000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const fetchDeliveryOrders = async () => {
    try {
      const res = await axios.get("https://irc.zentelex.com/api", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newData = res.data.data || [];

      // 🔔 notify only AFTER first fetch
      if (
        previousCount.current !== null &&
        newData.length > previousCount.current &&
        Notification.permission === "granted"
      ) {
        showNotification(
          "New Delivery Order 🚚",
          `${
            newData.length - previousCount.current
          } new delivery order(s) received`
        );
      }

      previousCount.current = newData.length;
      setDeliveryOrders(newData);
    } catch {
      alert("Failed to fetch delivery orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (id) => {
    setDetailsLoading(true);
    setSelectedOrder(null);

    try {
      const res = await axios.get(
        `https://irc.zentelex.com/api/delivery-orders/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data.data);
    } catch {
      alert("Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const approveOrder = async () => {
    if (!selectedOrder) return;

    setApproving(true);
    try {
      await axios.put(
        `https://irc.zentelex.com/api/${selectedOrder.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 🔔 approval notification
      if (Notification.permission === "granted") {
        showNotification(
          "Delivery Order Approved ✅",
          `Contract ${selectedOrder.contractNo} approved`
        );
      }

      setSelectedOrder({ ...selectedOrder, status: "APPROVED" });
      setDeliveryOrders((prev) =>
        prev.map((o) =>
          o.id === selectedOrder.id ? { ...o, status: "APPROVED" } : o
        )
      );
    } catch {
      alert("Approval failed");
    } finally {
      setApproving(false);
    }
  };

  return (
    <Layout>
      <div className="content-page">
        <div className="content">
          <div style={pagePadding}>
            <div style={card}>
              <div style={header}>📦 Delivery Orders</div>

              <div style={{ padding: 16 }}>
                {loading ? (
                  <div style={center}>Loading...</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={table}>
                      <thead>
                        <tr>
                          {[
                            "#",
                            "Client",
                            "Contract",
                            "Commodity",
                            "Qty",
                            "Status",
                            "Action",
                          ].map((h) => (
                            <th key={h} style={th}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {deliveryOrders.map((o, i) => (
                          <tr key={o.id} style={tr}>
                            <td style={td}>{i + 1}</td>
                            <td style={td}>{o.clientName}</td>
                            <td style={{ ...td, fontWeight: 600 }}>
                              {o.contractNo}
                            </td>
                            <td style={td}>{o.commodity}</td>
                            <td style={{ ...td, textAlign: "right" }}>
                              {o.doQuantity}
                            </td>
                            <td style={{ ...td, textAlign: "center" }}>
                              <span
                                style={{
                                  ...badge,
                                  background:
                                    o.status === "APPROVED"
                                      ? "#198754"
                                      : "#f0ad4e",
                                }}
                              >
                                {o.status || "PENDING"}
                              </span>
                            </td>
                            <td style={{ ...td, textAlign: "center" }}>
                              <button
                                style={viewBtn}
                                onClick={() => fetchOrderDetails(o.id)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {(detailsLoading || selectedOrder) && (
            <div style={overlay} onClick={() => setSelectedOrder(null)}>
              <div style={modal} onClick={(e) => e.stopPropagation()}>
                <div style={modalHeader}>
                  Delivery Order Details
                  <button
                    style={closeBtn}
                    onClick={() => setSelectedOrder(null)}
                  >
                    ✕
                  </button>
                </div>

                <div style={modalBody}>
                  {detailsLoading ? (
                    <div style={center}>Loading...</div>
                  ) : (
                    <>
                      <div style={detailsGrid}>
                        {Object.entries(selectedOrder).map(([k, v]) => (
                          <div key={k} style={detailRow}>
                            <div style={detailKey}>{formatLabel(k)}</div>
                            <div style={detailValue}>{v || "-"}</div>
                          </div>
                        ))}
                      </div>

                      <div style={modalFooter}>
                        <button
                          style={{
                            ...approveBtn,
                            opacity:
                              selectedOrder.status === "APPROVED" ? 0.6 : 1,
                          }}
                          disabled={
                            selectedOrder.status === "APPROVED" || approving
                          }
                          onClick={approveOrder}
                        >
                          {approving
                            ? "Approving..."
                            : selectedOrder.status === "APPROVED"
                            ? "Approved"
                            : "Approve"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

/* ================= HELPERS ================= */

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());

/* ================= STYLES ================= */

const pagePadding = { padding: 24 };
const center = { textAlign: "center", padding: 20 };

const card = {
  background: "#fff",
  borderRadius: 10,
  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
};

const header = {
  padding: "16px 20px",
  fontSize: 18,
  fontWeight: 600,
  borderBottom: "1px solid #eee",
};

const table = {
  width: "100%",
  minWidth: 900,
  borderCollapse: "collapse",
};

const th = {
  padding: 14,
  background: "#f8f9fa",
  fontWeight: 600,
};

const tr = { borderBottom: "1px solid #eee" };
const td = { padding: 14 };

const badge = {
  padding: "6px 14px",
  borderRadius: 20,
  fontSize: 12,
  color: "#fff",
  fontWeight: 600,
};

const viewBtn = {
  padding: "6px 14px",
  background: "#0d6efd",
  color: "#fff",
  border: 0,
  borderRadius: 20,
  cursor: "pointer",
};

const approveBtn = {
  padding: "10px 26px",
  background: "#198754",
  color: "#fff",
  border: 0,
  borderRadius: 6,
  fontSize: 15,
  cursor: "pointer",
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modal = {
  background: "#fff",
  width: 800,
  maxHeight: "85vh",
  borderRadius: 10,
  display: "flex",
  flexDirection: "column",
};

const modalHeader = {
  padding: 16,
  fontWeight: 600,
  borderBottom: "1px solid #eee",
  display: "flex",
  justifyContent: "space-between",
};

const modalBody = {
  padding: 16,
  overflowY: "auto",
};

const closeBtn = {
  background: "transparent",
  border: 0,
  fontSize: 18,
  cursor: "pointer",
};

const detailsGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 14,
};

const detailRow = { display: "flex", gap: 10 };
const detailKey = { width: "40%", fontWeight: 600, color: "#555" };
const detailValue = { width: "60%" };

const modalFooter = {
  marginTop: 24,
  paddingTop: 16,
  borderTop: "1px solid #eee",
  textAlign: "right",
};

export default DeliveryOrderList;
